import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useOAuthSession } from '@/features/auth';
// the store module, not the auth barrel: the barrel's dialogs import this feature.
import { useLinkOAuthAccount } from '@/features/auth/hooks/useLinkOAuthAccount';
import { useOAuthBindingDialogStore } from '@/features/auth/oauthBindingDialogStore';
import { PROVIDER_NAMES } from '@/features/auth/providerNames';
import {
    useCustodialBalance,
    useCustodialPanelState,
} from '@/features/custodial';
import { broadcastTradingSession } from '@/features/tradingSession/broadcast';
import { useTradingSessionStore } from '@/features/tradingSession/store';
import { useOnboardedWalletPresence } from '@/features/tradingSession/useOnboardedWalletPresence';
import { useAnchorChain, useAuth, useWallet } from '@/features/wallet';
// the helper module, not the wallet barrel: it is the same routine the
// wallet-switch dialog runs, so both ways out of a switch behave identically.
import { resolveSwitchToAddress } from '@/features/wallet/switchGuard/resolveSwitchToAddress';
import type { OnboardingQueueState } from '@/lib/api/onboarding';
import { queryKeys } from '@/lib/query';

import {
    claimsSocialLink,
    needsSelfProvisioning,
    onboardingEntryStep,
    shouldEvaluateOnboarding,
    shouldOpenWalletGate,
    stepAfterVoucher,
    stepAfterWalletSetup,
} from '../services/onboardingAdmission';
import {
    getOnboardingRecord,
    markOnboardingCompleted,
    markTermsAccepted,
    setOnboardingResumeReauth,
} from '../services/onboardingStorage';
import { useOnboardingLauncher } from '../store';

import { useAuthorizationWalletPresence } from './useAuthorizationWalletPresence';
import { useEoaProvisioning } from './useEoaProvisioning';
import { useOnboardingTradingWindow } from './useOnboardingTradingWindow';
import { useSnappyOnboarding } from './useSnappyOnboarding';

/** Queue states whose whole UI, including the rejoin, lives on the queue step. */
const QUEUE_STEP_STATES: readonly OnboardingQueueState[] = [
    'notRedeemed',
    'cancelled',
    'queued',
    'ready',
    'missed',
];

export type OnboardingStep =
    'welcome' | 'terms' | 'wallet' | 'voucher' | 'provision' | 'funding';

export const ONBOARDING_STEP_ORDER: readonly OnboardingStep[] = [
    'welcome',
    'terms',
    'wallet',
    'voucher',
    'provision',
    'funding',
];

/**
 * Orchestrates the first-login onboarding dialog.
 *
 * Opens once per wallet after the first successful sign-in and never again once
 * completed. Terms acceptance is the hard gate: declining or closing before
 * accepting logs the session out (back to demo); the wallet setup and funding
 * steps are optional, so closing after acceptance simply completes the flow.
 *
 * Two paths converge here. Social sign-in has its Snappy account created for it
 * by the admission queue; a wallet-only user signs the two provisioning legs
 * themselves in the `provision` step. Only an app token is required to reach
 * either — real mode comes later, once a trading window is opened.
 */
export function useOnboardingFlow() {
    const { runtimeMode, isAuthenticated, logout } = useAuth();
    const { address } = useWallet();
    const { anchorChainId } = useAnchorChain();
    const {
        isAuthenticated: isSocialAuthed,
        displayName,
        custodialWalletAddress,
        refresh: refreshOAuth,
        rebind: rebindOAuth,
    } = useOAuthSession();
    // The social login this run is creating an account for, if any. In memory
    // only: a reload drops it and the still-unbound cookie re-opens the binding
    // dialog, which is where the user chooses a route again.
    const associateSocial = useOnboardingLauncher((s) => s.associateSocial);
    const custodialBalance = useCustodialBalance();
    const refetchCustodialBalance = custodialBalance.refetch;
    const openDeposit = useCustodialPanelState((s) => s.openDeposit);
    const queryClient = useQueryClient();

    /**
     * Everything that reports "does this user have a Snappy Wallet".
     *
     * The agent-status read is the one nothing else refreshes: it has a 30s stale
     * window, no polling interval and no invalidation anywhere in onboarding, so
     * a user who just finished setup kept being told to finish setup until the
     * tab was refocused. The balance read self-heals on its own 20s refetch; this
     * one only ever healed by accident.
     */
    const refreshWalletReads = useCallback(() => {
        refetchCustodialBalance();
        void refreshOAuth();
        void queryClient.invalidateQueries({
            queryKey: queryKeys.speed.agentStatus(),
        });
        // the on-chain pair a just-finished ceremony created: cached for five
        // minutes, so without this the user stays "not onboarded" to every
        // surface that reads it right after finishing.
        void queryClient.invalidateQueries({
            queryKey: queryKeys.tradingSession.all,
        });
    }, [queryClient, refetchCustodialBalance, refreshOAuth]);

    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<OnboardingStep>('welcome');
    const [maxReachedIndex, setMaxReachedIndex] = useState(0);
    const evaluatedForRef = useRef<string | null>(null);
    // an externally launched flow (e.g. "Create account" in the sign-in dialog)
    // owns the dialog until the user leaves it: a new user has no wallet and no
    // app token yet, which is the state the flow exists to fix, not a reason to
    // tear it down.
    const externallyLaunchedRef = useRef(false);
    // terms accepted in this session before any wallet was connected
    const pendingTermsRef = useRef(false);
    // "the user got as far as You're all set", read by `finish` without making
    // the callback change identity on every step change.
    const onFundingStepRef = useRef(false);

    const isRealMode = runtimeMode === 'real';

    // A Snappy Wallet exists when the user is in real mode, the OAuth session
    // is bound to one, or the speed balance read succeeds.
    //
    // Real mode is the strongest of the three: a trading window is only minted
    // after BOTH custodial wallets are verified on chain, so it is proof the
    // wallets exist. The balance read is the weakest: its backend row is
    // created by the FIRST DEPOSIT, not at onboarding, so it errors with
    // `custodialWalletMissing` for every wallet-first user who has not
    // deposited yet. In real mode that error means "no first deposit yet",
    // never "no wallet" — reading it as the latter reopened the onboarding
    // dialog over users who had already finished. A partially onboarded user
    // (authorization wallet only) never reaches real mode, so this still
    // gates them.
    const hasReportedCustodialWallet =
        isRealMode ||
        !!custodialWalletAddress ||
        (!custodialBalance.isLoading &&
            !custodialBalance.isError &&
            custodialBalance.numericBalance !== null);

    // ...and the fourth, for the cohort none of the three can see: a
    // wallet-first user in demo mode. They hold no OAuth binding, their backend
    // balance row does not exist until a first deposit, and a lapsed trading
    // window drops them out of real mode on every reload, so all three signals
    // above said "no wallet" for a user who has both of them on chain — which
    // is what reopened the wizard over them after onboarding. The chain read is
    // the durable answer; it is skipped entirely when the cheap signals already
    // agree there is a wallet.
    const onChainWallets = useOnboardedWalletPresence(
        address,
        !hasReportedCustodialWallet,
    );
    const hasCustodialWallet =
        hasReportedCustodialWallet || onChainWallets.onboarded === true;

    // Only users who still need a wallet are subject to launch admission, so
    // only they poll for queue status, or read their registry entry. Existing
    // customers must not pay for a temporary gate that can never apply to them.
    const inWalletGate =
        (isRealMode || isAuthenticated) && !!address && !hasCustodialWallet;
    const onboarding = useSnappyOnboarding(inWalletGate);
    const requiresAdmission = onboarding.config.enabled && !hasCustodialWallet;
    // The wallet-first path: an app token with no social identity behind it, so
    // nobody else will create this user's wallets. An association run joins it:
    // the account belongs to the connected owner, who signs for it.
    const needsProvisioning = needsSelfProvisioning({
        isSocialAuthenticated: isSocialAuthed,
        hasCustodialWallet,
        hasSocialAssociationIntent: !!associateSocial,
    });

    // The durable on-chain half of "how far did this user get": read once per
    // address so a reload can resume the ceremony on the outstanding leg.
    const authorizationWallet = useAuthorizationWalletPresence(
        address,
        inWalletGate,
        anchorChainId,
    );

    const startTradingWalletCreation = onboarding.startEoaWalletCreation;
    const eoaProvisioning = useEoaProvisioning({
        startTradingWalletCreation,
        socialAccountHash: associateSocial?.accountHash ?? null,
        startSocialWalletCreation: onboarding.startSocialWalletCreation,
        queueStatus: onboarding.status,
        authorizationWalletExists: authorizationWallet.exists,
    });

    // Publish it so the deposit panel's "one deposit away" copy can stop
    // promising automatic wallet creation while the launch gate is on.
    const setAdmissionRequired = useOnboardingLauncher(
        (s) => s.setAdmissionRequired,
    );
    useEffect(() => {
        setAdmissionRequired(isRealMode && requiresAdmission);
    }, [isRealMode, requiresAdmission, setAdmissionRequired]);

    // Publish the dialog's open state so global watchers outside onboarding
    // (the wallet-switch guard) can stay silent while onboarding is the thing
    // asking the user to connect or switch wallets. Cleared on unmount: a
    // gate that goes away takes its dialog with it.
    const setOnboardingDialogOpen = useOnboardingLauncher(
        (s) => s.setDialogOpen,
    );
    useEffect(() => {
        setOnboardingDialogOpen(isOpen);
    }, [isOpen, setOnboardingDialogOpen]);
    useEffect(
        () => () => useOnboardingLauncher.getState().setDialogOpen(false),
        [],
    );

    // Open once per login for wallets that haven't completed onboarding.
    useEffect(() => {
        // losing the identity tears the flow down; merely becoming ineligible
        // (the wallet landed mid-flow) must not yank the dialog away.
        if (!address || (!isRealMode && !isAuthenticated)) {
            evaluatedForRef.current = null;
            if (!externallyLaunchedRef.current) setIsOpen(false);
            return;
        }
        // an external launch owns the step it opened at; the auto-open
        // evaluation must not pull the user back once their wallet lands.
        if (externallyLaunchedRef.current) return;
        if (
            !shouldEvaluateOnboarding({
                isRealMode,
                isAuthenticated,
                hasAddress: true,
                hasCustodialWallet,
            })
        )
            return;
        // Every durable input has to be in hand before picking a step: guessing
        // early and correcting later is what turned "resume" into "start over
        // at the queue and hope an effect bounces you back".
        if (
            !onboarding.configResolved ||
            !onboarding.statusResolved ||
            custodialBalance.isLoading ||
            !authorizationWallet.resolved ||
            // the chain read is the only signal that can still turn
            // `hasCustodialWallet` true; deciding before it lands is what
            // reopened the wizard over a finished wallet-first user.
            (!hasReportedCustodialWallet && !onChainWallets.resolved)
        )
            return;
        const key = address.toLowerCase();
        if (evaluatedForRef.current === key) return;
        evaluatedForRef.current = key;

        const record = getOnboardingRecord(address);
        if (
            !shouldOpenWalletGate({
                locallyCompleted: !!record.completedAt,
                featureEnabled: onboarding.config.enabled,
                hasCustodialWallet,
            })
        )
            return;
        // Resuming (e.g. back from the OAuth redirect) skips past the
        // already-accepted terms.
        const initialStep: OnboardingStep = onboardingEntryStep({
            termsAccepted: !!record.termsAcceptedAt,
            hasCustodialWallet,
            isSocialAuthenticated: isSocialAuthed,
            isWalletAuthenticated: isAuthenticated,
            hasSocialAssociationIntent: !!associateSocial,
            queueState: onboarding.config.enabled
                ? onboarding.status.state
                : null,
            authorizationWalletExists: authorizationWallet.exists,
        });
        setStep(initialStep);
        setMaxReachedIndex(ONBOARDING_STEP_ORDER.indexOf(initialStep));
        setIsOpen(true);
    }, [
        associateSocial,
        isRealMode,
        isAuthenticated,
        address,
        hasCustodialWallet,
        isSocialAuthed,
        onboarding.config.enabled,
        onboarding.configResolved,
        onboarding.statusResolved,
        onboarding.status.state,
        custodialBalance.isLoading,
        hasReportedCustodialWallet,
        onChainWallets.resolved,
        authorizationWallet.resolved,
        authorizationWallet.exists,
    ]);

    useEffect(() => {
        if (!address || !pendingTermsRef.current) return;
        pendingTermsRef.current = false;
        markTermsAccepted(address);
    }, [address]);

    // The launcher holds the resume intent in memory only, so closing the app
    // mid-onboarding would finish it into nothing. Persist it against the
    // wallet the moment there is one to key it against.
    const launcherResumeReauth = useOnboardingLauncher(
        (s) => s.resumeReauthOnFinish,
    );
    useEffect(() => {
        if (!address || !launcherResumeReauth) return;
        setOnboardingResumeReauth(address, true);
    }, [address, launcherResumeReauth]);

    useEffect(() => {
        onFundingStepRef.current = step === 'funding';
    }, [step]);

    // Track the furthest step reached so the progress bar can navigate back
    // (and forward again) to any already-visited step.
    useEffect(() => {
        setMaxReachedIndex((prev) =>
            Math.max(prev, ONBOARDING_STEP_ORDER.indexOf(step)),
        );
    }, [step]);

    // External launch requests (e.g. "Do the Setup" in the deposit panel, or the
    // trading-window dialog's account-setup recovery) open the dialog at the
    // requested step even after onboarding completed.
    const requestedStep = useOnboardingLauncher((s) => s.requestedStep);
    const clearRequestedStep = useOnboardingLauncher((s) => s.clear);
    const walletFirst = useOnboardingLauncher((s) => s.walletFirst);
    useEffect(() => {
        if (!requestedStep) return;
        clearRequestedStep();
        // every step past `wallet` needs an app token and a connected wallet, so
        // a launch that assumes either lands on the step that obtains them
        // instead of dead-ending. A launch with no wallet at all is still a
        // launch: `wallet` is where the user connects one.
        const target =
            (!address || (!isSocialAuthed && !isAuthenticated)) &&
            ONBOARDING_STEP_ORDER.indexOf(requestedStep) >
                ONBOARDING_STEP_ORDER.indexOf('wallet')
                ? 'wallet'
                : requestedStep;
        externallyLaunchedRef.current = true;
        setStep(target);
        setMaxReachedIndex((prev) =>
            Math.max(prev, ONBOARDING_STEP_ORDER.indexOf(target)),
        );
        setIsOpen(true);
    }, [
        requestedStep,
        clearRequestedStep,
        address,
        isAuthenticated,
        isSocialAuthed,
    ]);

    // Auto-skip the wallet-setup step when a Snappy Wallet already exists.
    useEffect(() => {
        if (isOpen && step === 'wallet' && hasCustodialWallet) {
            setStep('funding');
        }
    }, [isOpen, step, hasCustodialWallet]);

    useEffect(() => {
        if (
            isOpen &&
            step === 'voucher' &&
            (hasCustodialWallet || !onboarding.config.enabled)
        ) {
            setStep(stepAfterVoucher({ needsProvisioning }));
        }
    }, [
        hasCustodialWallet,
        isOpen,
        needsProvisioning,
        onboarding.config.enabled,
        step,
    ]);

    // Wallet-only users take the lane and then have to sign for it themselves.
    useEffect(() => {
        if (
            isOpen &&
            step === 'voucher' &&
            needsProvisioning &&
            onboarding.status.state === 'admitted'
        ) {
            setStep('provision');
        }
    }, [isOpen, needsProvisioning, onboarding.status.state, step]);

    // The provision step only exists while the lane is held. Every other queue
    // state has its own UI on the queue step, including the rejoin that wins a
    // lost lane back, so none of them may leave the user stranded here.
    useEffect(() => {
        if (!isOpen || step !== 'provision' || !onboarding.config.enabled) {
            return;
        }
        if (QUEUE_STEP_STATES.includes(onboarding.status.state)) {
            setStep('voucher');
        }
    }, [isOpen, onboarding.config.enabled, onboarding.status.state, step]);

    // Nobody who already has wallets, or whose wallets someone else creates,
    // belongs on the provision step. Once the ceremony has run it owns the step
    // until the user continues.
    useEffect(() => {
        if (!isOpen || step !== 'provision' || eoaProvisioning.hasStarted) {
            return;
        }
        // `needsProvisioning` is the whole cohort test: it is false for wallets
        // that already exist and for social users whose wallets someone else
        // creates, and true for the association run, which signs for its own.
        if (!needsProvisioning) setStep('funding');
    }, [eoaProvisioning.hasStarted, isOpen, needsProvisioning, step]);

    // Runs exactly once per transition INTO `completed`, never per render or
    // per callback identity. The ref is what makes this hold: a guard of
    // "state === completed" alone re-fires whenever any dependency changes
    // identity, and an unmemoized callback upstream once turned that into a
    // render → refresh → setMe → render loop that hammered /v2/auth/me tens of
    // thousands of times a minute. The deps are all stable today; this keeps
    // the effect safe if that ever stops being true.
    const walletReadsRefreshedRef = useRef(false);
    useEffect(() => {
        if (onboarding.status.state !== 'completed') {
            walletReadsRefreshedRef.current = false;
            return;
        }
        if (walletReadsRefreshedRef.current) return;
        walletReadsRefreshedRef.current = true;
        refreshWalletReads();
    }, [onboarding.status.state, refreshWalletReads]);

    // The association run's account now exists, but the social session still
    // reads as unbound: the app token upgrade a trading window needs only lands
    // once the session is rebound to the account. Same handshake, in the same
    // order, as the binding dialog performs after its own transaction.
    const associationHandshakeRef = useRef<string | null>(null);
    useEffect(() => {
        const accountHash = associateSocial?.accountHash;
        if (!accountHash || onboarding.status.state !== 'completed') return;
        if (associationHandshakeRef.current === accountHash) return;
        associationHandshakeRef.current = accountHash;
        void (async () => {
            try {
                const rebound = await rebindOAuth();
                if (rebound.bindingState !== 'bound') {
                    // release the guard so a later completion, or the binding
                    // dialog on the next load, can still finish this.
                    associationHandshakeRef.current = null;
                    return;
                }
                const binding = useOAuthBindingDialogStore.getState();
                binding.release(accountHash);
                binding.complete();
                broadcastTradingSession({ type: 'changed' });
            } catch {
                associationHandshakeRef.current = null;
            }
        })();
    }, [associateSocial, onboarding.status.state, rebindOAuth]);

    const goToTerms = useCallback(() => setStep('terms'), []);

    /** Progress-bar navigation — only to steps already reached. */
    const goToStep = useCallback(
        (index: number) => {
            if (index < 0 || index >= ONBOARDING_STEP_ORDER.length) return;
            if (index > maxReachedIndex) return;
            setStep(ONBOARDING_STEP_ORDER[index]);
        },
        [maxReachedIndex],
    );

    const acceptTerms = useCallback(() => {
        if (address) markTermsAccepted(address);
        // account creation accepts the terms before a wallet exists; the
        // acceptance is recorded against the wallet as soon as one connects.
        else pendingTermsRef.current = true;
        setStep(
            onboardingEntryStep({
                termsAccepted: true,
                hasCustodialWallet,
                isSocialAuthenticated: isSocialAuthed,
                isWalletAuthenticated: isAuthenticated,
                hasSocialAssociationIntent: !!associateSocial,
                queueState: onboarding.config.enabled
                    ? onboarding.status.state
                    : null,
                authorizationWalletExists: authorizationWallet.exists,
            }),
        );
    }, [
        address,
        associateSocial,
        authorizationWallet.exists,
        hasCustodialWallet,
        isAuthenticated,
        isSocialAuthed,
        onboarding.config.enabled,
        onboarding.status.state,
    ]);

    const dropResumeIntent = useCallback(() => {
        useOnboardingLauncher.getState().setResumeReauthOnFinish(false);
        if (address) setOnboardingResumeReauth(address, false);
    }, [address]);

    const decline = useCallback(() => {
        setIsOpen(false);
        externallyLaunchedRef.current = false;
        dropResumeIntent();
        useOnboardingLauncher.getState().setWalletFirst(false);
        useOnboardingLauncher.getState().setAssociateSocial(null);
        logout();
    }, [dropResumeIntent, logout]);

    const continueAfterWallet = useCallback(
        () =>
            setStep(
                stepAfterWalletSetup({ requiresAdmission, needsProvisioning }),
            ),
        [needsProvisioning, requiresAdmission],
    );

    const continueAfterVoucher = useCallback(
        () => setStep(stepAfterVoucher({ needsProvisioning })),
        [needsProvisioning],
    );

    // Continue to funding, with the first trading window signed on the way.
    // A wallet-first user arrives here with both wallets on chain and nothing
    // granting trading authority, so the deposit they are about to be offered
    // would first open the trading-window dialog over them. The signature is
    // asked for here instead, without the concept ever being shown; a decline
    // or failure keeps them on the step, and pressing Continue again re-asks.
    const tradingWindow = useOnboardingTradingWindow();
    const openTradingWindow = tradingWindow.openTradingWindow;
    const continueToFunding = useCallback(async () => {
        // a social session carries its own authority, and a live window needs
        // no second signature
        if (
            isSocialAuthed ||
            useTradingSessionStore.getState().hasActiveAuthority()
        ) {
            setStep('funding');
            return;
        }
        if (await openTradingWindow()) setStep('funding');
    }, [isSocialAuthed, openTradingWindow]);

    // A social login added from the funding step. It comes back from the
    // provider unbound, and for a first-time user still inside this wizard the
    // link is one more step of it (the regular binding dialog defers, see
    // `claimsSocialLink`). With an account on chain it is linked inline; a
    // first-timer with no account yet takes the association route instead,
    // which is what the binding dialog's "set up a new account" would do.
    const pendingBinding = useOAuthBindingDialogStore((s) => s.pending);
    const claimsLink =
        !!address &&
        !!pendingBinding &&
        claimsSocialLink({
            termsAccepted: !!getOnboardingRecord(address).termsAcceptedAt,
            locallyCompleted: !!getOnboardingRecord(address).completedAt,
        });
    const socialLinkHash =
        claimsLink && hasCustodialWallet ? pendingBinding.accountHash : null;
    const linkAccount = useLinkOAuthAccount(socialLinkHash);
    const walletReadsResolved =
        hasReportedCustodialWallet || onChainWallets.resolved;
    useEffect(() => {
        if (!claimsLink || hasCustodialWallet || !walletReadsResolved) return;
        useOnboardingLauncher.getState().setAssociateSocial({
            provider: pendingBinding.provider,
            accountHash: pendingBinding.accountHash,
        });
        useOAuthBindingDialogStore.getState().clear();
    }, [claimsLink, hasCustodialWallet, pendingBinding, walletReadsResolved]);
    // the wizard took the login from the regular dialog, so it has to be on
    // screen to show it, whatever the auto-open rules say about a finished
    // wallet (a lapsed window would otherwise leave the login pending unseen)
    useEffect(() => {
        if (!socialLinkHash || isOpen) return;
        setStep('funding');
        setMaxReachedIndex(ONBOARDING_STEP_ORDER.indexOf('funding'));
        setIsOpen(true);
    }, [isOpen, socialLinkHash]);
    const socialLink =
        socialLinkHash && address && pendingBinding
            ? {
                providerName: PROVIDER_NAMES[pendingBinding.provider],
                ownerAddress: address,
                accountWallet: linkAccount.accountWallet,
                isWorking: linkAccount.isWorking,
                error: linkAccount.error,
                onLink: () => void linkAccount.link(),
            }
            : null;

    const runProvisioning = eoaProvisioning.run;
    const startProvisioning = useCallback(() => {
        if (!address) return;
        void runProvisioning(address);
    }, [address, runProvisioning]);

    /**
     * Continue the ceremony on a lane won inside this session.
     *
     * A user whose Cronos leg already landed has one signature left, and after a
     * rejoin nobody asks for it: the queue hands the lane back, the step
     * re-mounts, and both sides wait for the other until the lane expires again.
     * Pressing "Rejoin the line" is the request, so the step acts on it.
     *
     * A lane that was already held when this session started is deliberately not
     * one: that is a reload, and prompting a wallet signature nobody asked for
     * is worse than showing the armed button underneath.
     */
    const queueState = onboarding.status.state;
    const admissionKey =
        queueState === 'admitted'
            ? (onboarding.status.admissionExpiresAt ?? 'admitted')
            : null;
    const lastQueueStateRef = useRef<OnboardingQueueState | null>(null);
    const resumableAdmissionRef = useRef<string | null>(null);
    useEffect(() => {
        // the first settled read is the baseline, never a transition: before it
        // lands the status still says `notRedeemed` by default, and reading that
        // as "the user just won a lane" would arm this on every reload.
        if (!inWalletGate || !onboarding.statusResolved) return;
        const previous = lastQueueStateRef.current;
        lastQueueStateRef.current = queueState;
        if (!admissionKey || previous === null || previous === 'admitted') {
            return;
        }
        resumableAdmissionRef.current = admissionKey;
    }, [admissionKey, inWalletGate, onboarding.statusResolved, queueState]);

    // Once per admission, and never over a run, a mismatch or a failure the
    // user still has to read.
    const autoStartedForRef = useRef<string | null>(null);
    const {
        authorizationWalletReady,
        tradingWalletReady,
        isRunning: provisioningIsRunning,
        detachedProvisioning,
        hasStarted: provisioningHasStarted,
        failure: provisioningFailure,
        mismatch: provisioningWalletMismatch,
    } = eoaProvisioning;
    useEffect(() => {
        if (!isOpen || step !== 'provision' || !admissionKey) return;
        if (resumableAdmissionRef.current !== admissionKey) return;
        if (autoStartedForRef.current === admissionKey) return;
        if (!needsProvisioning || hasCustodialWallet) return;
        // only the second leg is cheap enough to run unprompted: the first one
        // costs two signatures and starts the account from nothing.
        if (!authorizationWalletReady || tradingWalletReady) return;
        if (
            provisioningIsRunning ||
            detachedProvisioning ||
            provisioningHasStarted
        ) {
            return;
        }
        if (provisioningFailure || provisioningWalletMismatch) return;
        autoStartedForRef.current = admissionKey;
        startProvisioning();
    }, [
        admissionKey,
        authorizationWalletReady,
        detachedProvisioning,
        hasCustodialWallet,
        isOpen,
        needsProvisioning,
        provisioningFailure,
        provisioningHasStarted,
        provisioningIsRunning,
        provisioningWalletMismatch,
        startProvisioning,
        step,
        tradingWalletReady,
    ]);

    // The wallet moved to another account mid-setup and the preflight stopped
    // the ceremony. Leaving for that account is the same routine the
    // wallet-switch dialog runs: sign out, then route on whether the new EOA
    // already owns a Snappy account. A relaunched onboarding keeps this dialog
    // (it reopens at `welcome` for the new owner); the other routes take the
    // user somewhere else, so the dialog gets out of the way.
    const provisioningMismatch = eoaProvisioning.mismatch;
    const clearProvisioning = eoaProvisioning.reset;
    const switchToConnectedAccount = useCallback(async () => {
        const next = provisioningMismatch?.connectedAddress;
        if (!next) return;
        const route = await resolveSwitchToAddress(next);
        // whatever this ceremony recorded belonged to the account just left; a
        // relaunch for the new owner must not inherit its failure or its
        // "already started" state.
        clearProvisioning();
        // nor its progress: the relaunch opens at `welcome`, and terms
        // acceptance is per-address, so a progress bar that still reaches
        // `provision` would let the new owner skip accepting them.
        setMaxReachedIndex(0);
        if (route === 'onboarding') return;
        setIsOpen(false);
        externallyLaunchedRef.current = false;
    }, [clearProvisioning, provisioningMismatch]);

    // The admission expired under the provision step. Winning the lane back
    // moves the queue row out of the states this step is allowed to show, and
    // the routing effect above hands the user to the step that owns what
    // follows; once they are re-admitted they land back here.
    const rejoinQueue = onboarding.rejoin;
    const resetProvisioning = eoaProvisioning.reset;
    const rejoinFromProvision = useCallback(async () => {
        resetProvisioning();
        await rejoinQueue();
    }, [rejoinQueue, resetProvisioning]);

    const finish = useCallback(
        (options?: { openDeposit?: boolean }) => {
            if (address) markOnboardingCompleted(address);
            // a flow that ends without a queue transition (the wallet landed
            // mid-run, or the user finished on the funding step) still has to
            // clear the wallet-missing surfaces it was launched from.
            refreshWalletReads();
            setIsOpen(false);
            externallyLaunchedRef.current = false;
            if (options?.openDeposit) openDeposit();
            const launcher = useOnboardingLauncher.getState();
            launcher.setWalletFirst(false);
            // the run is over either way; a leftover intent would re-route a
            // later, unrelated onboarding into the association path.
            launcher.setAssociateSocial(null);
            // the persisted copy is what survives a reload, so an onboarding
            // that spanned an app restart still lands back in the ceremony.
            const resume =
                launcher.resumeReauthOnFinish ||
                (!!address &&
                    getOnboardingRecord(address).resumeReauthOnFinish);
            dropResumeIntent();
            // a login that was claimed by this wizard but never linked goes
            // back to the regular binding dialog, which re-reads the record
            const pending = useOAuthBindingDialogStore.getState().pending;
            if (pending) useOAuthBindingDialogStore.getState().request(pending);
            const session = useTradingSessionStore.getState();
            // the window Continue to funding signed is still live, so neither
            // ceremony below has anything left to ask for
            if (resume && !session.hasActiveAuthority()) {
                // the user only came here because a trading window dead-ended on
                // a missing account; put them back in that ceremony.
                session.requestConnectedWalletWindow();
                return;
            }
            // no prompt here: Continue to funding already signed the first
            // window, and a user without one is left in demo mode to sign in
            // when they choose to.
        },
        [address, dropResumeIntent, openDeposit, refreshWalletReads],
    );

    // Radix close (Esc / overlay click). Before terms acceptance closing is a
    // decline; afterwards the remaining steps are optional, so it completes.
    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (nextOpen) return;
            const termsAccepted = address
                ? !!getOnboardingRecord(address).termsAcceptedAt
                : false;
            if (
                termsAccepted &&
                requiresAdmission &&
                onboarding.status.state !== 'completed'
            ) {
                // minimized, not finished: drop the resume so a later,
                // unrelated completion does not reopen a stale ceremony.
                setIsOpen(false);
                externallyLaunchedRef.current = false;
                dropResumeIntent();
                useOnboardingLauncher.getState().setWalletFirst(false);
            } else if (termsAccepted) finish();
            else decline();
        },
        [
            address,
            decline,
            dropResumeIntent,
            finish,
            onboarding.status.state,
            requiresAdmission,
        ],
    );

    return {
        isOpen,
        step,
        maxReachedIndex,
        isSocialAuthed,
        isWalletAuthed: isAuthenticated,
        walletFirst,
        associateSocial,
        displayName,
        goToTerms,
        goToStep,
        acceptTerms,
        decline,
        continueAfterWallet,
        continueAfterVoucher,
        continueToFunding,
        socialLink,
        /** The signature Continue to funding is waiting on, and its last failure. */
        fundingWindow: {
            isOpening: tradingWindow.isOpening,
            error: tradingWindow.error,
        },
        onboarding,
        needsProvisioning,
        eoaProvisioning,
        startProvisioning,
        switchToConnectedAccount,
        rejoinFromProvision,
        hasCustodialWallet,
        finish,
        handleOpenChange,
    };
}
